import React, { FunctionComponent, memo, useContext, useMemo, useRef } from 'react'
import { SurveyContext } from '@context/SurveyContext'
import { OneOfManyQuestion } from '@models/Survey'
import GraphicCell from '../GraphicCell/GraphicCell'
import { useComponentSize } from '@hooks/useComponentSize'
import './OneOfManyGraphicInput.scss'
import { OneOfManyGraphicInputProps } from '../AnswerProps'
import { SurveyMode } from '@reducers/SurveyReducer'
import { AnswerType } from '@models/Survey/Answer'

const OneOfManyGraphicInput: FunctionComponent<OneOfManyGraphicInputProps> = ({ onSelection }) => {
  const { currentQuestion, setAnswer, answerObject, mode } = useContext(SurveyContext)
  const componentRef = useRef<HTMLDivElement>(null)

  const { width } = useComponentSize(componentRef)

  const answer = useMemo(() => {
    if (!currentQuestion?.id) {
      return
    }

    const { [currentQuestion?.id]: answer } = answerObject

    return answer
  }, [currentQuestion?.id, answerObject])

  const handleChoice = (choice: AnswerType) => {
    setAnswer(choice)
    if ([SurveyMode.SANDBOX, SurveyMode.SANDBOX_PORTRAIT].includes(mode)) {
      onSelection(choice)
    }
  }

  const columnSize = useMemo(
    () => (currentQuestion as OneOfManyQuestion)?.meta?.columnSize ?? 3,
    [(currentQuestion as OneOfManyQuestion)?.meta?.columnSize],
  )

  const size = useMemo(() => {
    if (width && (currentQuestion as OneOfManyQuestion)?.meta?.columnSize) {
      // size in px for each graphic cell must have to get columnSize column
      const widthPadding = 8 * 2
      const gapSize = 5.6
      const totalGapSize = (columnSize - 1) * gapSize
      return (1 / columnSize) * (width - widthPadding) - totalGapSize
    }
    return undefined
  }, [width])

  const showLabel = useMemo(
    () => (currentQuestion as OneOfManyQuestion).meta?.showLabel,
    [(currentQuestion as OneOfManyQuestion).meta],
  )

  const labelSize = useMemo(() => {
    if (columnSize > 1) {
      return 'medium'
    }

    return 'large'
  }, [columnSize])

  return (
    <div className="OneOfManyGraphicInput" ref={componentRef}>
      {(currentQuestion as OneOfManyQuestion)?.choices.map(({ graphicChoice, id, text }) => {
        return (
          graphicChoice && (
            <span className="graphic-choice" key={id} onClick={() => handleChoice(id)}>
              <GraphicCell
                selected={id === answer}
                graphicSrc={graphicChoice.image}
                size={size}
                label={text}
                showLabel={showLabel}
                labeSize={labelSize}
              />
            </span>
          )
        )
      })}
    </div>
  )
}

export default memo(OneOfManyGraphicInput)
